import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { faProcessorApi, bankStatementApi } from '@/services/api';
import { FASource } from '@/types/client';
import { Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { pageStyles } from '@/components/utils/PageLayout';
import { PageFrame, PageHeader, Surface, Button, FileDropzone, Note } from '@/components/ui';
import styles from './FAHoldingsProcess.module.css';

interface UploadZoneConfig {
    title: string;
    optionalLabel: string | undefined;
    file: File | null;
    setFile: React.Dispatch<React.SetStateAction<File | null>>;
    extensions: string[];
    errorMsg: string;
    subText: string;
    inputId: string;
    visible: boolean;
    disabled: boolean;
}

const UploadZone: React.FC<UploadZoneConfig & { loading: boolean }> = ({
    title, optionalLabel, file, setFile, extensions, errorMsg, subText, inputId, disabled, loading,
}) => {
    const handleFiles = (files: File[]) => {
        const selected = files[0];
        if (!selected) return;
        if (!extensions.some(ext => selected.name.endsWith(ext))) {
            toast.error(errorMsg);
            return;
        }
        setFile(selected);
    };

    return (
        <FileDropzone
            title={title}
            optionalLabel={optionalLabel}
            accept={extensions}
            onFiles={handleFiles}
            file={file}
            onRemove={() => setFile(null)}
            subText={subText}
            inputId={inputId}
            disabled={disabled}
            busy={loading}
        />
    );
};

const FAHoldingsProcess: React.FC = () => {
    const navigate = useNavigate();
    // `source` is the backend Scheme.fa_source key (e.g. "PMS", "SGAF"), empty until
    // the sources endpoint responds.
    const [source, setSource] = useState<string>('');
    const [sources, setSources] = useState<FASource[]>([]);
    const [portfolioFile, setPortfolioFile] = useState<File | null>(null);
    const [cashFile, setCashFile] = useState<File | null>(null);
    const [provisionsFile, setProvisionsFile] = useState<File | null>(null);
    const [skipEmail, setSkipEmail] = useState(false);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<{
        success: boolean;
        message: string;
        errors: string[];
    } | null>(null);

    const [uploadingStatement, setUploadingStatement] = useState(false);

    // Bumped after every pick so the native input remounts empty and the same
    // file can be chosen again (the input value used to be reset by hand).
    const [bankInputKey, setBankInputKey] = useState(0);

    const handleBankStatementUpload = async (files: File[]) => {
        if (files.length === 0) return;

        for (const file of files) {
            const fileNameLower = file.name.toLowerCase();
            if (!fileNameLower.endsWith('.xlsx') && !fileNameLower.endsWith('.xls') && !fileNameLower.endsWith('.csv')) {
                toast.error('Only .xlsx, .xls, or .csv files are allowed.');
                setBankInputKey(k => k + 1);
                return;
            }
        }

        setUploadingStatement(true);
        try {
            await bankStatementApi.uploadFile(files);
            toast.success('Bank statement uploaded successfully');
        } catch (error: unknown) {
            const data = (error as { response?: { data?: Record<string, unknown> } })?.response?.data;
            const message = typeof data?.error === 'string'
                ? data.error
                : Object.values(data ?? {}).flat().find((value): value is string => typeof value === 'string');
            toast.error(message || 'Bank statement upload failed');
        } finally {
            setUploadingStatement(false);
            setBankInputKey(k => k + 1);
        }
    };

    const currentSource = sources.find(s => s.value === source);
    const isApiBased = currentSource?.is_api_based ?? false;
    const acceptsXlsx = currentSource?.accepts_xlsx ?? false;
    const cashFileRequired = currentSource?.cash_file_required ?? true;
    const provisionsSupported = currentSource?.provisions_supported ?? false;

    useEffect(() => {
        let cancelled = false;
        faProcessorApi.getSources().then(data => {
            if (!cancelled) {
                setSources(data);
                if (data.length > 0) setSource(data[0].value);
            }
        }).catch(() => {
            if (!cancelled) toast.error('Failed to load FA sources');
        });
        return () => { cancelled = true; };
    }, []);

    // Each upload zone owns its accepted extensions, labels and disabled state:
    // a single source of truth shared by validation, the file picker and rendering.
    const uploadZones = useMemo<UploadZoneConfig[]>(() => {
        const xlsxLabel = acceptsXlsx ? 'CSV/XLSX' : 'CSV';
        const xlsxSubText = acceptsXlsx ? 'CSV, XLSX, XLS' : 'CSV only';
        const xlsxExtensions = acceptsXlsx ? ['.csv', '.xlsx', '.xls'] : ['.csv'];
        const xlsxError = acceptsXlsx ? 'Please upload a CSV or XLSX file' : 'Please upload a CSV file';
        return [
        {
            title: `Portfolio ${xlsxLabel} File`,
            optionalLabel: undefined,
            file: portfolioFile,
            setFile: setPortfolioFile,
            extensions: xlsxExtensions,
            errorMsg: xlsxError,
            subText: xlsxSubText,
            inputId: 'portfolio_upload',
            visible: true,
            disabled: false,
        },
        {
            title: `Cash ${xlsxLabel} File`,
            optionalLabel: !cashFileRequired ? '(Not Required)' : undefined,
            file: cashFile,
            setFile: setCashFile,
            extensions: xlsxExtensions,
            errorMsg: xlsxError,
            subText: xlsxSubText,
            inputId: 'cash_upload',
            visible: true,
            disabled: !cashFileRequired,
        },
        {
            title: 'Provisions CSV File',
            optionalLabel: '(Optional)',
            file: provisionsFile,
            setFile: setProvisionsFile,
            extensions: ['.csv'],
            errorMsg: 'Please upload a .csv file for Provisions',
            subText: 'CSV only',
            inputId: 'provisions_upload',
            visible: provisionsSupported,
            disabled: false,
        },
    ];
    }, [portfolioFile, cashFile, provisionsFile, acceptsXlsx, cashFileRequired, provisionsSupported]);

    const handleSourceChange = (newSource: string) => {
        setSource(newSource);
        const newSourceCaps = sources.find(s => s.value === newSource);
        if (!newSourceCaps?.provisions_supported) {
            setProvisionsFile(null);
        }
        if (newSourceCaps?.is_api_based) {
            setPortfolioFile(null);
            setCashFile(null);
            setProvisionsFile(null);
            return;
        }
        if (!newSourceCaps?.accepts_xlsx) {
            if (portfolioFile && (portfolioFile.name.endsWith('.xlsx') || portfolioFile.name.endsWith('.xls'))) {
                setPortfolioFile(null);
                toast.info('XLSX files are not allowed for this source. Portfolio file cleared.');
            }
            if (cashFile && (cashFile.name.endsWith('.xlsx') || cashFile.name.endsWith('.xls'))) {
                setCashFile(null);
                toast.info('XLSX files are not allowed for this source. Cash file cleared.');
            }
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (isApiBased && (portfolioFile || cashFile)) {
            setPortfolioFile(null);
            setCashFile(null);
            toast.error('Files cannot be submitted for API-based sources.');
            return;
        }

        if ((portfolioFile || cashFile) && cashFileRequired && (!portfolioFile || !cashFile)) {
            toast.error('Both Portfolio and Cash files are required for this source');
            return;
        }

        setLoading(true);
        setResult(null);

        try {
            const response = await faProcessorApi.processHoldings({
                source,
                portfolio_file: portfolioFile,
                cash_file: cashFile,
                provisions_file: provisionsSupported ? provisionsFile : null,
                skip_email: isApiBased ? false : skipEmail,
            });

            setResult({
                success: response.success,
                message: response.message,
                errors: response.errors || [],
            });

            if (response.success) {
                toast.success('Holdings processed successfully!');
            } else {
                toast.warning('Processing completed with some errors.');
            }
        } catch (err: any) {
            const errorMsg = err.response?.data?.message || 'Failed to process holdings';
            toast.error(errorMsg);
            setResult({
                success: false,
                message: errorMsg,
                errors: err.response?.data?.errors || [errorMsg],
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <PageFrame
            header={
                <PageHeader
                    title="FA Holdings Process"
                    subtitle="Fetch holdings from email or upload the files directly for processing."
                />
            }
        >
            <Surface title="Process FA Holdings" padded>
            <form onSubmit={handleSubmit}>
                {/* Settings list */}
                <div className={styles['settings-list']}>
                    {/* FA Source row */}
                    <div className={styles['setting-row']}>
                        <div className={styles['setting-info']}>
                            <span className={styles['setting-label']}>FA Source</span>
                            <span className={styles['setting-hint']}>
                                {isApiBased
                                    ? 'Select the FA source to process. Holdings will be fetched via API.'
                                    : 'Select the FA source to process. If no files are uploaded, it will attempt to fetch the latest report from email.'}
                            </span>
                        </div>
                        <select
                            id="fa_source"
                            value={source}
                            onChange={(e) => handleSourceChange(e.target.value)}
                            disabled={loading}
                            className={pageStyles.input}
                            style={{ width: 200, flex: 'none' }}
                        >
                            {sources.map(s => (
                                <option key={s.value} value={s.value}>{s.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Skip Email row */}
                    <div className={styles['setting-row']}>
                        <div className={styles['setting-info']}>
                            <span className={styles['setting-label']}>Skip Email Fetching</span>
                            <span className={styles['setting-hint']}>
                                {isApiBased
                                    ? 'Not applicable — holdings are fetched via API for this source.'
                                    : 'If yes, the processor will read directly from cloud storage without checking for new emails.'}
                            </span>
                        </div>
                        <select
                            value={String(skipEmail)}
                            onChange={(e) => setSkipEmail(e.target.value === 'true')}
                            disabled={loading || isApiBased}
                            className={pageStyles.input}
                            style={{ width: 200, flex: 'none' }}
                        >
                            <option value="false">No</option>
                            <option value="true">Yes</option>
                        </select>
                    </div>
                </div>

                {/* Upload zones — not applicable for API-based sources (e.g. SMA) */}
                {!isApiBased && (
                <div className={styles['upload-grid']}>
                    {uploadZones.filter(zone => zone.visible).map(zone => (
                        <UploadZone key={zone.inputId} {...zone} loading={loading} />
                    ))}
                </div>
                )}

                <div className={styles['form-actions']}>
                    <Button
                        type="submit"
                        variant="primary"
                        disabled={loading || !source}
                        icon={loading ? <Loader2 className={styles.spinner} size={16} /> : undefined}
                    >
                        {loading ? 'Processing…' : 'Start Processing'}
                    </Button>
                </div>
            </form>

            {result && (
                <div className={styles['result']}>
                    <Note tone={result.success ? 'success' : 'warning'}>
                        <strong>{result.message}</strong>
                        {(result.errors ?? []).length > 0 && (
                            <div className={styles['error-list']}>
                                <h4>Encountered Errors:</h4>
                                <ul>
                                    {(result.errors ?? []).map((err, idx) => (
                                        <li key={idx} dangerouslySetInnerHTML={{ __html: err }} />
                                    ))}
                                </ul>
                            </div>
                        )}
                    </Note>
                </div>
            )}

            </Surface>

            <Surface
                title="Upload Bank Statement (NRI)"
                meta="Filed under the last working date, same as FA Holdings"
                padded
            >
            <FileDropzone
                key={bankInputKey}
                accept={['.xlsx', '.xls', '.csv']}
                multiple
                onFiles={handleBankStatementUpload}
                inputId="bank_statement_upload"
                disabled={uploadingStatement}
                mainText={uploadingStatement ? 'Uploading...' : 'Click or drag & drop bank statement files'}
                subText={uploadingStatement ? undefined : 'Select one file per bank format — XLSX, XLS, CSV'}
            />
            </Surface>
        </PageFrame>
    );
};

export default FAHoldingsProcess;